import Comment from '@/models/Comment';
import DayworksCost, { DayworksType } from '@/models/DayworksCost';
import LabourDailyRecord from '@/models/LabourDailyRecord';
import PlantDailyRecord from '@/models/PlantDailyRecord';
import DayworksCostService from '@/services/DayworksCostService';
import { defineStore } from 'pinia';
import { useRepo } from 'pinia-orm';

export const useDayworksCostsStore = defineStore('dayworks_costs', {
  state: () => ({}),

  getters: {
    models: () => useRepo(DayworksCost),
  },

  actions: {
    async createDayworksCost(
      claimId: Id,
      request: ApiRequests['api.v1.subcontractor.claims.lite.dayworks.store'],
    ) {
      const [data, error] = await DayworksCostService.create(claimId, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        useRepo(Comment).save(data.comment);
      }

      return data;
    },

    async updateDayworksCost(id: Id, request: ApiRequests['api.v1.dayworks-costs.update']) {
      const [data, error] = await DayworksCostService.update(id, request);

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        useRepo(Comment).save(data.comment);
      }

      return data;
    },

    async deleteDayworksCost(id: Id) {
      const [data, error] = await DayworksCostService.delete(id);

      if(error) {
        throw error;
      }

      this.models.destroy(id);

      return data;
    },

    async saveSubcontractorCertified(
      projectId: Id,
      claimId: Id,
      dayworksCostId: Id,
      request: ApiRequests['api.v1.subcontractor.claims.dayworks.certified.store'],
    ) {
      const [data, error] = await DayworksCostService.subcontractorCertified(
        projectId,
        claimId,
        dayworksCostId,
        request,
      );

      if(error) {
        throw error;
      }

      this.models.save(data.cost);

      if(data.comment) {
        useRepo(Comment).save(data.comment);
      }

      return data;
    },

    async saveSubcontractorClaim(
      claimId: Id,
      signedParams: unknown,
      form: ApiRequests['api.v1.subcontractor.claims.dayworks.claim.store'],
    ) {
      const [data, error] = await DayworksCostService.subcontractorClaim(claimId, signedParams, form);

      if(error) {
        throw error;
      }

      this.models.save(data);

      return data;
    },

    async deleteSubcontractorClaim(
      claimId: Id,
      signedParams: unknown,
      form: ApiRequests['api.v1.subcontractor.claims.dayworks.claim.destroy'],
    ) {
      const [data, error] = await DayworksCostService.subcontractorDelete(claimId, signedParams, form);

      if(error) {
        throw error;
      }

      this.models.destroy(data.id);

      return data;
    },

    async fetchRecords(
      projectId: Id,
      claimId: Id,
      type: ObjectValues<typeof DayworksType>,
      itemId: Id,
      page = 1,
    ) {
      const [data, error] = type === DayworksType.labour ?
        await DayworksCostService.fetchLabourRecords(projectId, claimId, itemId, page) :
        await DayworksCostService.fetchPlantRecords(projectId, claimId, itemId, page);

      if(error) {
        throw error;
      }

      if(type === DayworksType.labour) {
        useRepo(LabourDailyRecord).save(data.data);
      } else {
        useRepo(PlantDailyRecord).save(data.data);
      }

      return data;
    },

    async exportDayworks(projectId: Id, claimId: Id) {
      const [data, error] = await DayworksCostService.export(projectId, claimId);

      if(error) {
        throw error;
      }

      return data;
    },
  },
});
